import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  List,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { getTimeFromTimeline } from "../../helpers/DateTime";
import {
  estimatedAnnualOperatingCost,
  estimatedAnnualVariableOperatingCost,
  facilityLoanMonths,
  purchaseTerms,
} from "../../helpers/Financials";
import {
  floorToTwoSignificantDigits,
  formatMoneyConcise,
  formatWatts,
} from "../../helpers/Format";
import { getFuelPricesPerMBTU } from "../../data/FuelPrices";
import {
  FUELS,
  MONTH_NAMES,
  MONTHS,
  TICKS_PER_YEAR,
  WEATHER_DEPENDENT_FUELS,
} from "../../Constants";
import { getHydroAvailability } from "../../data/HydroSites";
import { GENERATORS } from "../../data/Facilities";
import {
  DateType,
  GameType,
  GeneratorShoppingType,
  FuelNameType,
  LocationType,
  TickPresentFutureType,
} from "../../Types";
import { generateNewTimeline } from "../../reducers/Game";
import {
  resilienceBuildOptions,
  ResilienceBuildOptionType,
  ResilienceSelectionType,
  withResilienceOptions,
} from "../../helpers/Hazards";
import {
  STANDARD_GAS_DESIGN_MIN_TEMP_C,
  TRACKER_ANNUAL_ENERGY_MULTIPLIER,
} from "../../data/Hazards";
import {
  coldPackageEffect,
  formatDesignTemperature,
} from "../base/WeatherResilienceText";
import {
  expectedMonthlyOutputShape,
  ExpectedOutputShape,
} from "../../helpers/ExpectedOutput";
import { MANUAL_ENTRY, ManualEntryTitleType } from "../base/ManualEntries";
import {
  formatLargeMassValueConcise,
  formatMass,
  largeMassUnit,
} from "../../helpers/Units";
import ManualLink from "../base/ManualLink";
import { useUnits } from "../base/UnitsContext";
import BuildOptionCard from "../base/BuildOptionCard";
import {
  mostRecentBuiltSize,
  sliderTickToW,
  wToSliderTick,
} from "../../helpers/BuildSizing";
import PurchaseReviewDialog, {
  financingShortfallText,
} from "../base/PurchaseReviewDialog";
import {
  getBuildAvailability,
  getSiteInventory,
  siteCountLabel,
} from "../base/BuildAvailability";
import HydroPrimer, { useHydroPrimer } from "../base/HydroPrimer";
import { getScenario } from "../../data/Scenarios";
import BuildMetric from "../base/BuildMetric";
import ConstructionBuildHeader from "../base/ConstructionBuildHeader";
import Sparkline from "../base/Sparkline";

function percent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/**
 * Every card's line is drawn against the same ceiling, so a flat solar curve looks flat next to a
 * wind curve rather than being stretched to fill its own box. Rounded up to a tenth so the scale
 * doesn't wobble between forecasts. Hydro's inflow is left out: a wet month routinely tops its
 * nameplate, and letting that set the scale would flatten every other line on the list, so hydro
 * clips at the top edge instead and its caption carries the dry month.
 */
export function sharedOutputCeiling(
  shapes: (ExpectedOutputShape | undefined)[],
): number {
  const highest = Math.max(
    0,
    ...shapes.flatMap((shape) =>
      shape && shape.kind === "weather" ? shape.monthly : [],
    ),
  );
  return Math.min(1, Math.max(0.1, Math.ceil(highest * 10 - 1e-9) / 10));
}

function ExpectedOutputMetric(props: {
  shape?: ExpectedOutputShape;
  ceiling: number;
}): React.JSX.Element {
  const { shape, ceiling } = props;
  let caption = "";
  let chart: React.ReactNode = null;
  if (shape && shape.kind === "on-demand") {
    caption = "On demand";
    chart = (
      <Sparkline
        values={new Array(12).fill(ceiling)}
        domain={[0, ceiling]}
        width={96}
        height={24}
        stretch
        baseline
        dash
        ariaLabel="Available on demand."
      />
    );
  } else if (shape) {
    const highMonth = shape.monthly.reduce(
      (high, value, month) => (value > shape.monthly[high] ? month : high),
      0,
    );
    const low = shape.monthly[shape.lowMonth];
    const water = shape.kind === "water-inflow";
    caption = `Low ${MONTHS[shape.lowMonth]} ${percent(low)}${water ? " water in" : ""}`;
    chart = (
      <Sparkline
        values={shape.monthly}
        domain={[0, ceiling]}
        width={96}
        height={24}
        stretch
        baseline
        fill
        lowMarker
        ariaLabel={`Typical year${water ? " of water inflow" : ""}: highest in ${MONTH_NAMES[highMonth]} at ${percent(shape.monthly[highMonth])}, lowest in ${MONTH_NAMES[shape.lowMonth]} at ${percent(low)}.`}
      />
    );
  }
  return (
    <div className="buildOptionMetric buildOptionOutput">
      <Typography variant="caption" color="textSecondary" component="div">
        {caption}
      </Typography>
      <div className="buildOptionSparkline">{chart}</div>
    </div>
  );
}

/**
 * Hydro can only deliver the water that arrives, so its typical output follows the forecast
 * inflow rather than the design capacity factor, which a drought year can fall well short of.
 */
function typicalCapacityFactor(
  generator: GeneratorShoppingType,
  shape?: ExpectedOutputShape,
): number {
  return shape?.kind === "water-inflow" ? shape.mean : generator.capacityFactor;
}

function GeneratorDetailRow(props: {
  label: string;
  value: React.ReactNode;
  entry: ManualEntryTitleType;
}): React.JSX.Element {
  return (
    <TableRow>
      <TableCell component="th" scope="row" className="generatorDetailLabel">
        {props.label}
      </TableCell>
      <TableCell align="right" className="generatorDetailValue">
        {props.value}
      </TableCell>
      <TableCell className="generatorDetailHelp">
        <ManualLink entry={props.entry} label={props.label.toLowerCase()} />
      </TableCell>
    </TableRow>
  );
}

interface GeneratorBuildItemProps {
  hydroAvailability?: ReturnType<typeof getHydroAvailability>;
  onUseSiteMaximum?: (peakW: number) => void;
  onUseMaxSize?: (peakW: number) => void;
  cash: number;
  date: DateType;
  interestRate: number;
  generator: GeneratorShoppingType;
  location: LocationType;
  seed: number;
  secondaryMetric?: string;
  advantages?: string[];
  /** Typical-year output; computed from the generator alone when omitted (on-demand plants only) */
  outputShape?: ExpectedOutputShape;
  outputCeiling?: number;
  compared?: boolean;
  compareDisabled?: boolean;
  onCompare?: () => void;
  // Optional build upgrades, and the quote with a selection of them. Both come from the hazard
  // model so the dialog's price is exactly the one the purchase will charge.
  resilienceOptions?: ResilienceBuildOptionType[];
  withResilience?: (
    selection: ResilienceSelectionType,
  ) => GeneratorShoppingType;
  onBuild: (financed: boolean, selection?: ResilienceSelectionType) => void;
}

export function GeneratorBuildItem(
  props: GeneratorBuildItemProps,
): React.JSX.Element {
  const { generator, cash } = props;
  const units = useUnits();
  const wideLayout = useMediaQuery("(min-width:600px)");
  const kgCO2ePerBtu = FUELS[generator.fuel]?.kgCO2ePerBtu ?? 0;
  const fuelPrices = getFuelPricesPerMBTU(
    props.date,
    props.seed,
    props.location,
  );
  const [open, setOpen] = React.useState(false);
  const resilienceOptions = props.resilienceOptions ?? [];
  const hasOptions = resilienceOptions.length > 0 && !!props.withResilience;
  const defaultSelection = (): ResilienceSelectionType =>
    Object.fromEntries(
      resilienceOptions.map((option) => [option.upgrade, option.selected]),
    );
  const [resilienceSelection, setResilienceSelection] =
    React.useState<ResilienceSelectionType>(defaultSelection);
  // A default hardening option is only a default: the card stays buyable whenever the plant
  // without it is affordable, and the dialog lets the player clear it
  const cheapestQuote =
    hasOptions && props.withResilience ? props.withResilience({}) : generator;
  const { downpayment } = purchaseTerms(
    cheapestQuote.buildCost,
    true,
    props.interestRate,
  );
  const includedOptions = resilienceOptions.filter((option) => option.selected);
  const sizeBuildable = props.generator.peakW <= props.generator.maxPeakW;
  const maxSizeW = floorToTwoSignificantDigits(generator.maxPeakW);
  const { buildable, secondaryText, offerMaxSize } = getBuildAvailability({
    hydroAvailability: props.hydroAvailability,
    name: generator.name,
    description: generator.description,
    available: generator.available,
    sizeBuildable,
    maxSizeLabel: formatWatts(maxSizeW),
    location: props.location,
    viableLocationsRemaining: generator.viableLocationsRemaining,
  });
  const sites = props.hydroAvailability
    ? undefined
    : getSiteInventory(
        generator.name,
        props.location,
        generator.viableLocationsRemaining,
      );
  const shortfall = financingShortfallText(cash, downpayment);
  const canBuild = buildable && !shortfall;
  const buildSubtitle = (buildable && shortfall) || secondaryText;
  const hasVariableOM = generator.variableOperatingCostPerMWh !== undefined;
  const estimatedVariableOM = estimatedAnnualVariableOperatingCost(generator);
  // kg of CO2 equivalent released per MWh generated - 0 for carbon-free sources,
  // whose fuel either isn't in FUELS at all (sun, wind) or is emission-free (uranium)
  const kgCO2ePerMWh = Math.round(1000000 * generator.btuPerWh * kgCO2ePerBtu);
  const constructionKgco2eTotal =
    (generator.constructionKgco2ePerW || 0) * generator.peakW;
  const outputShape =
    props.outputShape || expectedMonthlyOutputShape(generator, []);
  const waterShape =
    outputShape?.kind === "water-inflow" ? outputShape : undefined;
  const capacityFactor = typicalCapacityFactor(generator, outputShape);
  const typicalOutputW = generator.peakW * capacityFactor;
  // A short role tag stays on the card; how to use it waits for the details
  const [role, roleHint] =
    generator.fuel === "Hydro"
      ? [
          "Flexible water supply",
          "Rain and snow refill the reservoir; generation drains it.",
        ]
      : WEATHER_DEPENDENT_FUELS.includes(generator.fuel)
        ? ["Weather-dependent supply", "Pair with backup or storage."]
        : generator.gasCycle === "simple"
          ? ["Fast-start, costly per MWh", "Covers peaks and backup."]
          : generator.gasCycle === "combined"
            ? ["Efficient, slow to start", "Best run steadily for hours."]
            : generator.spinMinutes > 60
              ? ["Steady supply", "Best for demand that lasts for hours."]
              : ["Fast response", "Can follow changing demand."];
  const openReview = (e: React.SyntheticEvent) => {
    setResilienceSelection(defaultSelection());
    setOpen(true);
    e.stopPropagation();
  };

  // The dialog prices the quote with the player's hardening choice; the card keeps the default
  const quote =
    hasOptions && props.withResilience
      ? props.withResilience(resilienceSelection)
      : generator;
  const quoteCanBuild =
    buildable &&
    purchaseTerms(quote.buildCost, true, props.interestRate).downpayment <=
      cash;
  const resilienceOptionId = React.useId();
  // Each line under an option says what it buys; a warning line says what it costs the player
  const optionDetails = (
    option: ResilienceBuildOptionType,
  ): { text: string; warning?: boolean }[] => {
    switch (option.upgrade) {
      case "solarTrackers":
        return [
          {
            text: "Follows the sun for about 20% more morning and evening power.",
          },
          { text: "Stows steeply in hail. Can't be added after building." },
        ];
      case "hailResistant":
        return [];
      default: {
        if (!props.withResilience) return [];
        const standardMinTempC =
          props.withResilience({}).resilience?.designMinTempC ??
          STANDARD_GAS_DESIGN_MIN_TEMP_C;
        const lines: { text: string; warning?: boolean }[] = [
          {
            text: coldPackageEffect(
              props.withResilience({ [option.upgrade]: true }).resilience
                ?.designMinTempC ?? standardMinTempC,
              standardMinTempC,
              units,
            ),
          },
        ];
        if (option.defaultSelected) {
          lines.push({
            text: `Recommended here: winters often drop below ${formatDesignTemperature(standardMinTempC, units)}.`,
          });
        }
        return lines;
      }
    }
  };
  const anySelected = resilienceOptions.some(
    (option) => resilienceSelection[option.upgrade],
  );
  const affordabilityWarning =
    anySelected && !quoteCanBuild && canBuild
      ? resilienceOptions.length > 1
        ? "Uncheck options to afford the downpayment."
        : "Uncheck to afford the downpayment."
      : undefined;

  const compareAction = props.onCompare && canBuild && (
    <Button
      size="small"
      variant={props.compared ? "contained" : "text"}
      aria-pressed={props.compared}
      aria-label={`Compare ${generator.name}`}
      disabled={props.compareDisabled && !props.compared}
      onClick={(event) => {
        event.stopPropagation();
        props.onCompare?.();
      }}
    >
      Compare
    </Button>
  );

  const siteMaximum =
    props.hydroAvailability?.selected || props.hydroAvailability?.largest;
  const useSiteMaximumAction = siteMaximum &&
    generator.peakW !== siteMaximum.maxPeakW &&
    props.onUseSiteMaximum && (
      <Button
        size="small"
        onClick={() => props.onUseSiteMaximum?.(siteMaximum.maxPeakW)}
      >
        Use site maximum
      </Button>
    );
  const useMaxSizeAction = offerMaxSize &&
    generator.peakW !== maxSizeW &&
    props.onUseMaxSize && (
      <Button size="small" onClick={() => props.onUseMaxSize?.(maxSizeW)}>
        Use max size
      </Button>
    );
  // A resize shortcut sits under Review, so the context line moves up beside it
  const sizeAction = useSiteMaximumAction || useMaxSizeAction;
  const context = (
    <>
      {role}
      {sites && sites.remaining > 0 && (
        <>
          {" · "}
          <span className="nowrap">{siteCountLabel(sites)}</span>
        </>
      )}
    </>
  );

  const iconSrc = `/images/${generator.name.toLowerCase()}.svg`;
  // Nothing left to build: keep the card as a quiet one-line entry rather than a full pitch
  if (props.hydroAvailability?.remaining.length === 0) {
    return (
      <BuildOptionCard
        name={generator.name}
        iconSrc={iconSrc}
        review={{
          ariaLabel: `Review purchase of ${generator.name}`,
          disabled: true,
          onClick: () => undefined,
        }}
        context="No remaining buildable locations"
      />
    );
  }

  return (
    <BuildOptionCard
      name={generator.name}
      iconSrc={iconSrc}
      compared={props.compared}
      review={{
        ariaLabel: `Review purchase of ${generator.name}`,
        disabled: !canBuild,
        onClick: openReview,
      }}
      headerActions={wideLayout && compareAction}
      sizeAction={sizeAction}
      context={context}
      summary={
        props.hydroAvailability && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="body2">
              {props.hydroAvailability.remaining.length} sites left ·{" "}
              {props.hydroAvailability.eligible.length}{" "}
              {props.hydroAvailability.eligible.length === 1 ? "fits" : "fit"}
              {props.hydroAvailability.largest &&
                ` · largest: ${formatWatts(props.hydroAvailability.largest.maxPeakW, 6)}`}
            </Typography>
            {props.hydroAvailability.selected && (
              <Typography variant="body2">
                Site: {props.hydroAvailability.selected.name} ·{" "}
                {formatWatts(props.hydroAvailability.selected.maxPeakW, 6)} max
              </Typography>
            )}
          </Box>
        )
      }
      warning={canBuild ? undefined : buildSubtitle}
      metrics={
        <>
          <BuildMetric
            label="Build cost"
            value={formatMoneyConcise(generator.buildCost)}
            note={
              includedOptions.length
                ? `Incl. ${includedOptions.map((option) => option.label.toLowerCase()).join(", ")}`
                : undefined
            }
          />
          <BuildMetric
            label="Build time"
            value={`${Math.round(generator.yearsToBuild * 12)} mo`}
          />
          <ExpectedOutputMetric
            shape={outputShape}
            ceiling={props.outputCeiling || 1}
          />
          {props.secondaryMetric === "lcWh" && (
            <BuildMetric
              label="Cost per MWh"
              value={`${fuelPrices[generator.fuel] ? "~" : ""}${formatMoneyConcise(generator.lcWh * 1000000)}`}
            />
          )}
        </>
      }
      footerActions={!wideLayout && compareAction}
      details={
        <>
          <Typography
            className="buildOptionDescription"
            variant="body2"
            color="textSecondary"
          >
            {roleHint} {generator.description}
          </Typography>
          {(props.advantages || []).length > 0 && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                sx={{ flexWrap: "wrap" }}
                role="group"
                aria-label="Generator advantages"
              >
                {(props.advantages || []).map((advantage) => (
                  <Chip key={advantage} size="small" label={advantage} />
                ))}
              </Stack>
            </Box>
          )}
          <TableContainer>
            <Table
              size="small"
              aria-label="generator properties"
              className="generatorDetails"
            >
              <TableBody>
                {props.secondaryMetric !== "lcWh" && (
                  <GeneratorDetailRow
                    label="Lifetime cost"
                    value={
                      formatMoneyConcise(generator.lcWh * 1000000) + "/MWh"
                    }
                    entry={MANUAL_ENTRY.TOTAL_COST_OF_ENERGY}
                  />
                )}
                <GeneratorDetailRow
                  // Lifetime cost still assumes average water years; this is the year ahead
                  label={
                    waterShape
                      ? "Capacity factor, this forecast"
                      : "Expected capacity factor"
                  }
                  value={percent(capacityFactor)}
                  entry={MANUAL_ENTRY.CAPACITY_FACTOR}
                />
                {generator.minimumStableOutput !== undefined && (
                  <GeneratorDetailRow
                    label="Minimum stable output"
                    value={
                      percent(generator.minimumStableOutput) +
                      " · " +
                      formatWatts(
                        generator.peakW * generator.minimumStableOutput,
                      )
                    }
                    entry={MANUAL_ENTRY.RAMP_RATE}
                  />
                )}
                <GeneratorDetailRow
                  label={hasVariableOM ? "Fixed O&M" : "Base O&M"}
                  value={
                    formatMoneyConcise(generator.annualOperatingCost) + "/yr"
                  }
                  entry={MANUAL_ENTRY.OPERATING_COSTS}
                />
                {hasVariableOM && (
                  <>
                    <GeneratorDetailRow
                      label="Variable O&M"
                      value={
                        "$" +
                        (generator.variableOperatingCostPerMWh || 0).toFixed(
                          2,
                        ) +
                        "/MWh"
                      }
                      entry={MANUAL_ENTRY.OPERATING_COSTS}
                    />
                    <GeneratorDetailRow
                      label="Expected variable O&M"
                      value={formatMoneyConcise(estimatedVariableOM) + "/yr"}
                      entry={MANUAL_ENTRY.OPERATING_COSTS}
                    />
                  </>
                )}
                {generator.costPerStart !== undefined && (
                  <GeneratorDetailRow
                    label="Non-fuel start cost"
                    value={
                      formatMoneyConcise(generator.costPerStart) + "/start"
                    }
                    entry={MANUAL_ENTRY.OPERATING_COSTS}
                  />
                )}
                {(hasVariableOM || generator.costPerStart !== undefined) && (
                  <GeneratorDetailRow
                    label="Estimated annual O&M"
                    value={
                      formatMoneyConcise(
                        estimatedAnnualOperatingCost(generator),
                      ) + "/yr"
                    }
                    entry={MANUAL_ENTRY.OPERATING_COSTS}
                  />
                )}
                {fuelPrices[generator.fuel] && (
                  <GeneratorDetailRow
                    label="Fuel costs"
                    value={
                      formatMoneyConcise(
                        generator.btuPerWh * (fuelPrices[generator.fuel] ?? 0),
                      ) + "/MWh"
                    }
                    entry={MANUAL_ENTRY.FUEL_COSTS}
                  />
                )}
                {generator.spinMinutes > 1 && (
                  <GeneratorDetailRow
                    label="Ramp up/down time"
                    value={generator.spinMinutes + " min"}
                    entry={MANUAL_ENTRY.RAMP_RATE}
                  />
                )}
                <GeneratorDetailRow
                  label="Accounting lifetime"
                  value={generator.lifespanYears + " years"}
                  entry={MANUAL_ENTRY.ACCOUNTING_LIFETIME}
                />
                {sites && (
                  <GeneratorDetailRow
                    label="Sites left"
                    value={`${sites.remaining} of ${sites.total}`}
                    entry={MANUAL_ENTRY.PROJECT_SITES}
                  />
                )}
                <GeneratorDetailRow
                  label="Direct emissions"
                  value={formatMass(kgCO2ePerMWh, units) + "/MWh"}
                  entry={MANUAL_ENTRY.EMISSIONS}
                />
                <GeneratorDetailRow
                  label="Construction emissions"
                  value={`${formatLargeMassValueConcise(
                    constructionKgco2eTotal,
                    units,
                  )} ${largeMassUnit(units)}`}
                  entry={MANUAL_ENTRY.EMISSIONS}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </>
      }
    >
      <PurchaseReviewDialog
        open={open}
        onClose={() => setOpen(false)}
        title={
          <>
            Build{" "}
            {formatWatts(generator.peakW, props.hydroAvailability ? 6 : 1)}{" "}
            {generator.name}?
          </>
        }
        cash={cash}
        buildCost={quote.buildCost}
        interestRate={props.interestRate}
        loanMonths={facilityLoanMonths(quote.lifespanYears)}
        cashDisabled={!buildable}
        loanDisabled={!buildable}
        upkeepPerMonth={estimatedAnnualOperatingCost(quote) / 12}
        upkeepDetail={
          fuelPrices[generator.fuel]
            ? "Plus fuel and loan payments."
            : "Plus loan payments."
        }
        onlineInMonths={Math.round(generator.yearsToBuild * 12)}
        onPurchase={(financed) => {
          props.onBuild(financed, hasOptions ? resilienceSelection : undefined);
          setOpen(false);
        }}
        preface={
          <>
            {props.hydroAvailability?.selected && (
              <Typography variant="body2" sx={{ px: 2, mb: 2 }}>
                {props.hydroAvailability.selected.name} ·{" "}
                {formatWatts(props.hydroAvailability.selected.maxPeakW, 6)} max.
                Uses the whole site. Only cancelling before completion frees it.
              </Typography>
            )}
            {hasOptions &&
              resilienceOptions.map((option, index) => {
                const describedBy = `${resilienceOptionId}-${option.upgrade}`;
                const lines = optionDetails(option);
                if (
                  affordabilityWarning &&
                  index === resilienceOptions.length - 1
                )
                  lines.push({ text: affordabilityWarning, warning: true });
                return (
                  <Box className="resilienceBuildOption" key={option.upgrade}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!resilienceSelection[option.upgrade]}
                          onChange={(event) =>
                            setResilienceSelection((selection) => ({
                              ...selection,
                              [option.upgrade]: event.target.checked,
                            }))
                          }
                          slotProps={{
                            input: { "aria-describedby": describedBy },
                          }}
                        />
                      }
                      label={`${option.label} +${formatMoneyConcise(option.extraBuildCost)}`}
                    />
                    <Box id={describedBy}>
                      {lines.map((line) => (
                        <Typography
                          key={line.text}
                          variant="body2"
                          color={line.warning ? undefined : "textSecondary"}
                          className={
                            line.warning
                              ? "resilienceBuildOptionDetail resilienceBuildOptionWarning"
                              : "resilienceBuildOptionDetail"
                          }
                        >
                          {line.text}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                );
              })}
          </>
        }
        extraFacts={[
          {
            concept: "supply",
            label: "Typical output",
            // The selected quote's trackers add their morning and evening energy
            value: `+${formatWatts(
              typicalOutputW *
                (quote.resilience?.solarTrackers
                  ? TRACKER_ANNUAL_ENERGY_MULTIPLIER
                  : 1),
            )}`,
            detail: waterShape
              ? `${formatWatts(generator.peakW)} max; water limits it, lowest in ${MONTH_NAMES[waterShape.lowMonth]}.`
              : `${formatWatts(generator.peakW)} max; weather may limit it.`,
          },
          ...(sites
            ? [
                {
                  concept: "build" as const,
                  label: "Project site",
                  value:
                    sites.remaining === 1
                      ? "Uses your last site"
                      : `Leaves ${sites.remaining - 1} of ${sites.total}`,
                  detail: "Each project takes one site, whatever its size.",
                },
              ]
            : []),
          {
            concept: kgCO2ePerMWh > 0 ? "danger" : "goal",
            label: "Direct emissions",
            value:
              kgCO2ePerMWh > 0
                ? `${formatMass(kgCO2ePerMWh, units)}/MWh`
                : "No direct emissions modeled",
          },
        ]}
      />
    </BuildOptionCard>
  );
}

function GeneratorComparison(props: {
  generators: GeneratorShoppingType[];
  outputShapes: Map<string, ExpectedOutputShape | undefined>;
  onClear: () => void;
}): React.JSX.Element | null {
  if (props.generators.length === 0) {
    return null;
  }
  return (
    <section className="generatorComparison" aria-label="Generator comparison">
      <div className="generatorComparisonHeader">
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Comparing {props.generators.length}/3
        </Typography>
        <Button size="small" onClick={props.onClear}>
          Clear
        </Button>
      </div>
      <div className="generatorComparisonChoices">
        {props.generators.map((generator) => (
          <div className="generatorComparisonChoice" key={generator.name}>
            <img
              src={`/images/${generator.name.toLowerCase()}.svg`}
              alt=""
              aria-hidden
            />
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {generator.name}
            </Typography>
            <Typography variant="caption">
              {formatMoneyConcise(generator.buildCost)} ·{" "}
              {Math.round(generator.yearsToBuild * 12)} mo
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatWatts(
                generator.peakW *
                  typicalCapacityFactor(
                    generator,
                    props.outputShapes.get(generator.name),
                  ),
              )}{" "}
              typical · {formatMoneyConcise(generator.lcWh * 1000000)}/MWh
            </Typography>
          </div>
        ))}
      </div>
    </section>
  );
}

type GeneratorSortKey = "buildCost" | "yearsToBuild" | "lcWh";

const sortOptions: ReadonlyArray<readonly [GeneratorSortKey, string]> = [
  ["buildCost", "Build Cost"],
  ["yearsToBuild", "Build Time"],
  ["lcWh", "Cost per MWh"],
];

function valueLabelFormat(x: number) {
  return formatWatts(sliderTickToW(x));
}

interface BuildForecast {
  timeline: TickPresentFutureType[];
  windSpeeds: number[];
  offshoreWindSpeeds: number[];
  airborneWindSpeeds: number[];
  solarIrradiances: number[];
}

// The three-year forecast is the one expensive part of a quote (tens of milliseconds), and it
// reads nearly all of the game: the clock, fleet, customers, loans, weather position and story
// state. So it is keyed on the game object itself rather than a hand-picked subset of it. Redux
// hands back a new object whenever anything in the game changes and the same object otherwise,
// which makes the key exact: the size slider, sort, compare and details reuse the forecast, and
// so do remounts (StrictMode's double render, switching catalog tabs) while the game is unchanged.
// Only the per-size GENERATORS quote and output shapes, both cheap, rerun on a slider move.
// Weak, so a replaced game state is collected along with its forecast.
const buildForecasts = new WeakMap<GameType, BuildForecast>();

function getBuildForecast(
  game: GameType,
  now: TickPresentFutureType,
): BuildForecast {
  const cached = buildForecasts.get(game);
  if (cached) {
    return cached;
  }
  const timeline = generateNewTimeline(
    game,
    now.cash,
    now.customers,
    TICKS_PER_YEAR * 3,
  );
  const forecast: BuildForecast = {
    timeline,
    windSpeeds: timeline.map((w) => w.windKph),
    offshoreWindSpeeds: timeline.flatMap((w) =>
      w.windOffshoreKph === undefined ? [] : [w.windOffshoreKph],
    ),
    airborneWindSpeeds: timeline.map((w) => w.windAirborneKph),
    solarIrradiances: timeline.map((w) => w.solarIrradianceWM2),
  };
  buildForecasts.set(game, forecast);
  return forecast;
}

export interface StateProps {
  evidenceRequest?: import("../../Types").EvidenceRequestType;
  facilityDragActive?: boolean;
  game: GameType;
  focusFuel?: FuelNameType;
}

export interface DispatchProps {
  onEvidenceReady?: (
    request: import("../../Types").EvidenceRequestType,
    element: HTMLElement | null,
  ) => void;
  onBuildGenerator: (
    generator: GeneratorShoppingType,
    financed: boolean,
  ) => void;
  onBack: () => void;
}

export type Props = StateProps & DispatchProps;

export default function BuildGenerators(props: Props): React.JSX.Element {
  const { evidenceRequest, facilityDragActive, onEvidenceReady } = props;
  const evidenceAnchor = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const request = evidenceRequest;
    if (
      request &&
      typeof request.target === "object" &&
      request.target.card === "FACILITIES" &&
      request.target.view === "BUILD_GENERATORS" &&
      !facilityDragActive
    ) {
      onEvidenceReady?.(request, evidenceAnchor.current);
    }
  }, [evidenceRequest, facilityDragActive, onEvidenceReady]);
  const { game, onBack } = props;
  const now = getTimeFromTimeline(game.date.minute, game.timeline);
  const [sliderTick, setSliderTick] = React.useState<number>(() =>
    wToSliderTick(mostRecentBuiltSize(game.facilities, false)),
  );
  // Sizes between slider ticks, chosen per generator: hydro's site maximum or a technology's cap
  const [exactSizes, setExactSizes] = React.useState<Record<string, number>>(
    {},
  );
  const [sort, setSort] = React.useState<GeneratorSortKey>("buildCost");
  const [comparedNames, setComparedNames] = React.useState<string[]>([]);
  const [primerVisible, dismissPrimer] = useHydroPrimer();

  if (!now) {
    return <span />;
  }

  const cash = now.cash;
  const {
    timeline: forecastedTimeline,
    windSpeeds,
    offshoreWindSpeeds,
    airborneWindSpeeds,
    solarIrradiances,
  } = getBuildForecast(game, now);
  const hydroAvailability = getHydroAvailability(
    game,
    exactSizes.Hydro ?? sliderTickToW(sliderTick),
  );
  const shownGenerators = GENERATORS(
    game,
    sliderTickToW(sliderTick),
    windSpeeds,
    solarIrradiances,
    offshoreWindSpeeds,
    airborneWindSpeeds,
  )
    .map((generator) =>
      exactSizes[generator.name] !== undefined
        ? GENERATORS(
            game,
            exactSizes[generator.name],
            windSpeeds,
            solarIrradiances,
            offshoreWindSpeeds,
            airborneWindSpeeds,
          ).find((candidate) => candidate.name === generator.name) || generator
        : generator,
    )
    .filter(
      (generator) =>
        game.scenarioId !== 1 ||
        game.tutorialStep !== 1 ||
        ["Natural Gas", "Sun", "Wind"].includes(generator.fuel),
    );
  // Technologies that burn the same fuel stay side by side, placed among the rest by their better
  // member, so the gas peaker and combined cycle read as one choice of how to burn gas
  const bestInFuel = new Map<string, number>();
  shownGenerators.forEach((generator) =>
    bestInFuel.set(
      generator.fuel,
      Math.min(bestInFuel.get(generator.fuel) ?? Infinity, generator[sort]),
    ),
  );
  const generators = shownGenerators.sort((a, b) => {
    // Hydro with no sites left always sinks to the bottom
    const spentA =
      a.name === "Hydro" && hydroAvailability.remaining.length === 0;
    const spentB =
      b.name === "Hydro" && hydroAvailability.remaining.length === 0;
    if (spentA !== spentB) {
      return spentA ? 1 : -1;
    }
    if (props.focusFuel && a.fuel !== b.fuel) {
      if (a.fuel === props.focusFuel) {
        return -1;
      }
      if (b.fuel === props.focusFuel) {
        return 1;
      }
    }
    const groupA = bestInFuel.get(a.fuel) as number;
    const groupB = bestInFuel.get(b.fuel) as number;
    if (a.fuel !== b.fuel && groupA !== groupB) {
      return groupA - groupB;
    }
    return a[sort] - b[sort];
  });
  const outputShapes = new Map(
    generators.map((generator) => [
      generator.name,
      expectedMonthlyOutputShape(generator, forecastedTimeline),
    ]),
  );
  const outputCeiling = sharedOutputCeiling(Array.from(outputShapes.values()));
  const buildableGenerators = generators.filter(
    (generator) => generator.available && generator.peakW <= generator.maxPeakW,
  );
  const lowestBuildCost = Math.min(
    ...buildableGenerators.map((generator) => generator.buildCost),
  );
  const fastestBuild = Math.min(
    ...buildableGenerators.map((generator) => generator.yearsToBuild),
  );
  const lowestEnergyCost = Math.min(
    ...buildableGenerators.map((generator) => generator.lcWh),
  );
  const comparedGenerators = generators.filter((generator) =>
    comparedNames.includes(generator.name),
  );

  // Tutorials script their own path through this list, so the primer waits for a real game
  const hydro = generators.find((generator) => generator.name === "Hydro");
  const hydroSites = {
    total: hydroAvailability.remaining.length,
    remaining: hydroAvailability.remaining.length,
  };
  const showPrimer =
    primerVisible &&
    !!hydro?.available &&
    !!hydroSites &&
    hydroSites.remaining > 0 &&
    !getScenario(game.scenarioId, game.customScenario)?.tutorialSteps;

  const toggleCompare = (name: string) => {
    setComparedNames((current) =>
      current.includes(name)
        ? current.filter((candidate) => candidate !== name)
        : current.length < 3
          ? [...current, name]
          : current,
    );
  };

  return (
    <div
      className="flexContainer screenCatalog"
      ref={evidenceAnchor}
      tabIndex={-1}
      aria-label="Generator build options"
    >
      {props.focusFuel &&
        !buildableGenerators.some(
          (generator) => generator.fuel === props.focusFuel,
        ) && (
          <Typography role="status" sx={{ px: 2, py: 1 }}>
            The requested fuel has no available generator at this size. Showing
            generator options.
          </Typography>
        )}
      <ConstructionBuildHeader
        capacity={valueLabelFormat(sliderTick)}
        sliderValue={sliderTick}
        sliderMin={0}
        sliderMax={34}
        sort={sort}
        sortOptions={sortOptions}
        onSliderChange={(value) => {
          setSliderTick(value);
          setExactSizes({});
        }}
        onSortChange={(value) => setSort(value as GeneratorSortKey)}
      />
      <GeneratorComparison
        generators={comparedGenerators}
        outputShapes={outputShapes}
        onClear={() => setComparedNames([])}
      />
      <List dense className="scrollable cardList">
        {generators.map((g: GeneratorShoppingType) => {
          const advantages = [
            g.buildCost === lowestBuildCost ? "Lowest upfront cost" : undefined,
            g.yearsToBuild === fastestBuild ? "Fastest online" : undefined,
            g.lcWh === lowestEnergyCost ? "Lowest lifetime cost" : undefined,
            g.btuPerWh === 0 ? "No direct emissions" : undefined,
          ].filter((value): value is string => Boolean(value));
          const compared = comparedNames.includes(g.name);
          return (
            <React.Fragment key={g.name}>
              {showPrimer && g.name === "Hydro" && hydroSites && (
                <HydroPrimer onDismiss={dismissPrimer} />
              )}
              <GeneratorBuildItem
                hydroAvailability={
                  g.name === "Hydro" ? hydroAvailability : undefined
                }
                onUseSiteMaximum={(peakW) => {
                  setSliderTick(wToSliderTick(peakW));
                  setExactSizes((sizes) => ({ ...sizes, Hydro: peakW }));
                }}
                onUseMaxSize={(peakW) => {
                  setSliderTick(Math.max(0, wToSliderTick(peakW)));
                  setExactSizes((sizes) => ({ ...sizes, [g.name]: peakW }));
                }}
                date={game.date}
                seed={game.seed}
                location={game.location}
                interestRate={game.interestRate}
                generator={g}
                cash={cash}
                secondaryMetric={sort === "buildCost" ? "yearsToBuild" : sort}
                advantages={advantages.slice(0, 2)}
                outputShape={outputShapes.get(g.name)}
                outputCeiling={outputCeiling}
                compared={compared}
                compareDisabled={comparedNames.length >= 3}
                onCompare={() => toggleCompare(g.name)}
                resilienceOptions={resilienceBuildOptions(g, game)}
                withResilience={(selection) =>
                  withResilienceOptions(g, game, selection)
                }
                onBuild={(financed, selection) => {
                  props.onBuildGenerator(
                    selection === undefined
                      ? g
                      : withResilienceOptions(g, game, selection),
                    financed,
                  );
                  onBack();
                }}
              />
            </React.Fragment>
          );
        })}
      </List>
    </div>
  );
}
